import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import {
  matchReferencedEntities,
  type ConnectionTagProps,
  type PlaceholderTagProps,
  type SkillTagProps,
} from "../../utils/entityMatching";
import { IntegrationIcon } from "../IntegrationIcon";

// Define the IntegrationEnhanced type for the cache data
interface IntegrationEnhanced {
  platform: string;
  metadata?: {
    image_url?: string;
  };
}

/**
 * SkillTag component for rendering skill references
 */
const SkillTag: React.FC<SkillTagProps> = ({ skillName }) => {
  return <span className="font-bold">/{skillName}</span>;
};

/**
 * PlaceholderTag component for rendering placeholder references
 */
const PlaceholderTag: React.FC<PlaceholderTagProps> = ({ placeholder }) => {
  return <span className="font-bold">{placeholder}</span>;
};

/**
 * Safe ConnectionTag component that reads from React Query cache with graceful fallback
 */
const ConnectionTag: React.FC<ConnectionTagProps> = ({ connection }) => {
  try {
    const queryClient = useQueryClient();
    const integrations = queryClient.getQueryData<IntegrationEnhanced[]>([
      "orgIntegrations",
    ]);
    const integration = integrations?.find(
      (integration) => integration.platform === connection.platform,
    );

    if (integration?.metadata?.image_url) {
      return (
        <span className="relative text-primary pl-1">
          <IntegrationIcon
            imageURL={integration.metadata.image_url}
            platform={connection.platform}
            name={connection.title}
            className="inline w-[18px] h-[18px] relative top-[-2px] mr-1"
          />
          <strong>{connection.title}</strong>
        </span>
      );
    }
  } catch {
    // Query client not available or other error, use graceful fallback
  }

  // Graceful fallback: render just the connection title
  return <strong>{connection.title}</strong>;
};

export interface InlineEntityProps {
  children?: React.ReactNode;
}

const components = {
  SkillTag,
  PlaceholderTag,
  ConnectionTag,
};

/**
 * InlineEntity component that processes text content and renders entity references
 * as inline components (skills, connections, placeholders)
 */
export const InlineEntity = React.memo(({ children }: InlineEntityProps) => {
  if (typeof children === "string") {
    return matchReferencedEntities(children, components);
  }

  if (Array.isArray(children)) {
    return children.map((child, index) => {
      if (typeof child === "string") {
        return (
          <React.Fragment key={index}>
            {matchReferencedEntities(child, components)}
          </React.Fragment>
        );
      }
      return child;
    });
  }

  return children;
});

InlineEntity.displayName = "InlineEntity";
